import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { CartProvider } from "@/components/CartContext";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { UpdateBanner } from "@/components/UpdateBanner";
import AppLayout from "@/components/layout/AppLayout";
import DisplayNameModal from "@/components/DisplayNameModal";
import HomePage from "@/pages/HomePage";
import LiveEventsPage from "@/pages/LiveEventsPage";
import EventsPage from "@/pages/EventsPage";
import GivingPage from "@/pages/GivingPage";
import RentalPage from "@/pages/RentalPage";
import RentalContractPage from "@/pages/RentalContractPage";
import RentalCancelPage from "@/pages/RentalCancelPage";
import RentalDepositPage from "@/pages/RentalDepositPage";
import RentalBalancePage from "@/pages/RentalBalancePage";
import AboutPage from "@/pages/AboutPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import VideosPage from "@/pages/VideosPage";
import AuthCallback from "@/pages/AuthCallback";
import CustomPage from "@/pages/CustomPage";
import CollectivesPage from "@/pages/CollectivesPage";
import CollectiveWelcomePage from "@/pages/CollectiveWelcomePage";
import PageRequestPage from "@/pages/PageRequestPage";
import AllAccessPollPage from "@/pages/AllAccessPollPage";
import LinksPage from "@/pages/LinksPage";
import FormsPage from "@/pages/FormsPage";
import FormSubmitPage from "@/pages/FormSubmitPage";
import DashPage from "@/pages/DashPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Code-split: heavy pages loaded on demand
const BookstorePage = lazy(() => import("@/pages/BookstorePage"));
const CollectiveRoomPage = lazy(() => import("@/pages/CollectiveRoomPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ApostolicDashboard = lazy(() => import("@/pages/ApostolicDashboard"));
const ApostleDen = lazy(() => import("@/pages/ApostleDen"));
const ApostleMessage = lazy(() => import("@/pages/ApostleMessage"));
const MediaBiblePage = lazy(() => import("@/pages/MediaBiblePage"));
const ElishaTimesReaderPage = lazy(() => import("@/pages/ElishaTimesReaderPage"));
const MePage = lazy(() => import("@/pages/MePage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const HostDashboardPage = lazy(() => import("@/pages/HostDashboardPage"));
// Hidden Training Hub — not wired into any navigation, reachable only by direct URL
const ManualsHubPage = lazy(() => import("@/pages/ManualsHubPage"));
const ManualPage = lazy(() => import("@/pages/ManualPage"));

function LazyFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="lazy-loading-fallback">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-navy/20 border-t-navy rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Loading…</span>
      </div>
    </div>
  );
}

function AdminMovedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bone px-4" data-testid="admin-moved-page">
      <div className="max-w-sm w-full text-center">
        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
          <span className="text-gold text-2xl font-heading">404</span>
        </div>
        <h1 className="font-heading text-2xl text-navy mb-2">Page Moved</h1>
        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
          This page has moved to <strong>/wa-ops-center</strong> — please update your bookmark.
        </p>
      </div>
    </div>
  );
}

function CollectiveRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/collectives/${slug}/room`} replace />;
}

// LEGACY RESCUE ONLY: catches old fabricated wagc.cc/c/{code} shortlinks already
// texted out (wagc.cc path-preserves to /c/{code}). New messages use MTA's branded
// short links — do not route new links through /c/. Room resolves by slug or id.
function CollectiveShortlinkRedirect() {
  const { code } = useParams();
  return <Navigate to={`/collectives/${code}/room`} replace />;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  const { user, needsDisplayName, setUser } = useAuth();

  // Track engagement: count visits to Events and Collectives pages
  useEffect(() => {
    const qualifying = ["/live-events", "/collectives", "/events"];
    if (qualifying.includes(location.pathname)) {
      const current = parseInt(localStorage.getItem("wagc_engage_count") || "0", 10);
      if (current < 3) {
        localStorage.setItem("wagc_engage_count", String(current + 1));
      }
      window.dispatchEvent(new CustomEvent("wagc-engage"));
    }
  }, [location.pathname]);

  // Detect session_id in URL fragment synchronously — before any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {/* Display Name modal — shown globally when a logged-in user hasn't customized their display name */}
      {needsDisplayName && !location.hash?.includes("session_id=") && (
        <DisplayNameModal
          defaultName={user?.name || ""}
          onSave={(displayName) => setUser({ ...user, display_name: displayName, display_name_customized: true })}
        />
      )}
      <ErrorBoundary>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            {/* Bottom nav primary routes */}
            <Route path="/live-events" element={<LiveEventsPage />} />
            <Route path="/collectives" element={<CollectivesPage />} />
            <Route path="/collectives/:slug/welcome" element={<CollectiveWelcomePage />} />
            <Route path="/collectives/:slug/room" element={<CollectiveRoomPage />} />
            <Route path="/collectives/:slug" element={<CollectiveRedirect />} />
            <Route path="/c/:code" element={<CollectiveShortlinkRedirect />} />
            <Route path="/host/:slug" element={<HostDashboardPage />} />
            <Route path="/media" element={<MediaBiblePage />} />
            <Route path="/elisha-times/:id" element={<ElishaTimesReaderPage />} />
            <Route path="/me" element={<MePage />} />
            {/* Legacy routes — keep working for backward compat */}
            <Route path="/events" element={<EventsPage />} />
            <Route path="/videos" element={<VideosPage />} />
            {/* Other pages */}
            <Route path="/bookstore" element={<BookstorePage />} />
            <Route path="/giving" element={<GivingPage />} />
            <Route path="/rental" element={<RentalPage />} />
            <Route path="/rental/contract/:token" element={<RentalContractPage />} />
            <Route path="/rental/cancel/:token" element={<RentalCancelPage />} />
            <Route path="/rental/deposit/:token" element={<RentalDepositPage />} />
            <Route path="/rental/balance/:token" element={<RentalBalancePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/admin" element={<AdminMovedPage />} />
            <Route path="/wa-ops-center" element={<AdminPage />} />
            <Route path="/dashboard" element={<ApostolicDashboard />} />
            <Route path="/den" element={<ApostleDen />} />
            <Route path="/apostle-message/:uuid" element={<ApostleMessage />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/p/:slug" element={<CustomPage />} />
            <Route path="/request-page" element={<PageRequestPage />} />
            <Route path="/all-access" element={<AllAccessPollPage />} />
            <Route path="/links" element={<LinksPage />} />
            <Route path="/forms" element={<FormsPage />} />
            <Route path="/forms/:slug" element={<FormSubmitPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/dash" element={<DashPage />} />
            {/* Hidden Training Hub — login-gated inside the page, not linked in nav */}
            <Route path="/manuals" element={<ManualsHubPage />} />
            <Route path="/manuals/:slug" element={<ManualPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppLayout>
            <AppRouter />
          </AppLayout>
          <Toaster richColors position="top-right" />
          <UpdateBanner />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
